import {DataContext} from "../../contexts/dataContext";
import {useContext, useEffect, useState} from "react";
import {IVehicleSearch} from "../../interfaces/VehicleSearch";
import {basePrice} from "../MakeReservation/pricingCalculations";

export enum FuelType {
    All = 'all_fuel',
    Petrol = 'e95',
    Diesel = 'diesel',
    Electric = 'electric',
    Hybrid = 'hybrid'
}

export enum VehicleType {
    Compact = 'compact', // url param: acriss=1
    StationWagon = 'stw', // ""=2
    SUV = 'suv', // ""=3
    Minibus = 'minibus', // ""=4
    Van = 'van', // ""=5
}

export enum Country  {
    All = 'all_finland',
    Baltics = 'baltics',
    Scandinavia = 'scandinavia',
    Europe = 'europe'
}

export enum Transmission {
    All = "all_vehicleType",
    Automatic = 'automatic',
    Manual = 'manual'
}

export enum NonCategorisedOptions {
    ParkingPermit = 'parkingPermit',
    TowingHitch = 'towingHitch',
    ECO = 'eco',
    Parkinghall = 'parkinghall'
}

export enum TyreTypes {
    All = "all_tyreTypes",
    StuddedTyres = 'studdedTyres',
    SummerTyres = 'summerTyres',
    AllSeason = 'allSeasonTyres'
}

export enum SortOptions {
    Price = 'price',
    Distance = 'distance',
    TrunkVolume = 'trunkvolume',
    Length = 'length',
    Models = 'models'
}

export interface IAcrissCodes {
    compact: string[];
    stw: string[];
    suv: string[];
    minibus: string[];
    van: string[];
}

export interface ITagCloudAvailableCountry {
    baltics: string[];
    scandinavia: string[];
    europe: string[];
}

export enum  ResultsTypes {
    All = 'allResults',
    modelsOnly = 'modelResults',
}

export interface FilterParameters {
    types?: VehicleType[];
    fuelType?: FuelType;
    tyreTypesNew?: TyreTypes;
    selectedTag?: Country;
    transmission?: Transmission;
    otherTypes?: NonCategorisedOptions[];
    reset?: boolean;
    model?: number[] | undefined;
    resultsType?: ResultsTypes;
}

export const tagCloudAvailableCountry:  ITagCloudAvailableCountry = {
    baltics:  ["Viro", "Latvia", "Liettua", "Estonia", "Latvia", "Lithuania"],
    scandinavia: ["Sweden", "Norway", "Denmark","Ruotsi", "Norja", "Tanska"],
    europe: ["Saksa", "Ranska", "Itävalta", "Sveitsi", "Belgia", "Alankomaat", "Luxemburg", "Germany", "France", "Austria", "Switzerland", "Belgium", "Netherlands", "Luxembourg"],
};

/**
 * Pure filter: the only place that decides which vehicles a set of FilterParameters admits.
 *
 * It used to live inside useFilter, closed over that hook instance's `originalList` copy of
 * mapData. Three components then published competing lists into dataContext.filteredVehicles
 * (this hook's handleFilters, VehicleSearch's post-search initializer, and SearchTab's sort),
 * and after a re-search the last writer won with an unfiltered list — the customer's filters
 * silently stopped applying. Every writer now derives its list from this one function, so they
 * cannot disagree no matter which order the effects happen to run in.
 */
export const applyFilterParameters = (
    list: IVehicleSearch[],
    {types, fuelType, tyreTypesNew, selectedTag, transmission, otherTypes, model, resultsType}: FilterParameters = {},
    acrissCodeSet?: IAcrissCodes,
    noVatPrices?: boolean
): IVehicleSearch[] => {
    let newFilteredList: IVehicleSearch[] = list.filter((vehicle) => {
        let isValid = true;

        if (model?.length) {
            isValid = isValid && model.some(modelId => vehicle.vehicleModelData.id === modelId);
        }

        if (types && types.length > 0) {
            isValid = isValid && types.some(type => {
                switch (type) {
                    case VehicleType.StationWagon:
                        return acrissCodeSet?.stw.includes(vehicle.vehicleModelData.modelacriss);
                    case VehicleType.SUV:
                        return acrissCodeSet?.suv.includes(vehicle.vehicleModelData.modelacriss);
                    case VehicleType.Minibus:
                        return acrissCodeSet?.minibus.includes(vehicle.vehicleModelData.modelacriss);
                    case VehicleType.Van:
                        return (vehicle.vehicleModelData.type === "van");
                    case VehicleType.Compact:
                        return acrissCodeSet?.compact.includes(vehicle.vehicleModelData.modelacriss);
                    default:
                        return false;
                }
            });
        }

        if (fuelType) {
            // diesel -> fuelType=0, gasoline -> fuelType=1, electric -> fuelType=2
            // hybrid -> 'none'/'plug-in'/'normal'
            switch (fuelType) {
                case FuelType.Diesel:
                    isValid = isValid && vehicle.vehicleData.fuelType === 0
                        && (vehicle.vehicleData.hybridity === 'none' || vehicle.vehicleData.hybridity === 'normal');
                    break;
                case FuelType.Petrol:
                    isValid = isValid && vehicle.vehicleData.fuelType === 1;
                    break;
                case FuelType.Hybrid:
                    isValid = isValid &&
                        (vehicle.vehicleData.hybridity === 'normal' || vehicle.vehicleData.hybridity === 'plug-in');
                    break;
                case FuelType.Electric:
                    isValid = isValid && vehicle.vehicleData.fuelType === 2;
                    break;
            }
        }

        if (selectedTag) {
            switch (selectedTag) {
                case Country.Baltics:
                    isValid = isValid && (vehicle.vehicleData.exportPermits.balticStates === true || vehicle.tagCloud.some(tag => tagCloudAvailableCountry.baltics.includes(tag)));
                    break;
                case Country.Scandinavia:
                    isValid = isValid && (vehicle.vehicleData.exportPermits.scandinavia === true || vehicle.tagCloud.some(tag => tagCloudAvailableCountry.scandinavia.includes(tag)));
                    break;
                case Country.Europe:
                    isValid = isValid && (vehicle.vehicleData.exportPermits.europe === true || vehicle.tagCloud.some(tag => tagCloudAvailableCountry.europe.includes(tag)));
                    break;
            }
        }

        if (tyreTypesNew) {
            switch (tyreTypesNew) {
                case TyreTypes.StuddedTyres:
                    isValid = isValid && vehicle.vehicleData.tyreType === 'nasta';
                    break;
                case TyreTypes.SummerTyres:
                    isValid = isValid && vehicle.vehicleData.tyreType === 'kesä';
                    break;
                case TyreTypes.AllSeason:
                    isValid = isValid && vehicle.vehicleData.tyreType === 'kitka';
                    break;
            }
        }

        if (transmission) {
            switch (transmission) {
                case Transmission.Automatic:
                    isValid = isValid && vehicle.vehicleData.hasAutomaticTransmission;
                    break;
                case Transmission.Manual:
                    isValid = isValid && !vehicle.vehicleData.hasAutomaticTransmission;
                    break;
            }
        }

        if (otherTypes && otherTypes.length > 0) {
            if (otherTypes.includes(NonCategorisedOptions.ParkingPermit)) {
                isValid = (isValid && vehicle.parkingPermit.valid);
            }

            if (otherTypes.includes(NonCategorisedOptions.TowingHitch)) {
                isValid = !!(isValid && vehicle.vehicleData.hasHitch);
            }

            if (otherTypes.includes(NonCategorisedOptions.ECO)) {
                isValid = isValid && (vehicle.vehicleData.hybridity === 'normal' ||
                    vehicle.vehicleData.hybridity === 'plug-in'
                    || vehicle.vehicleData.fuelType === 2);
            }

            if (otherTypes.includes(NonCategorisedOptions.Parkinghall)) {
                isValid = (isValid && vehicle.homeLocationData.inside === true);
            }
        }

        return isValid;
    });

    if (resultsType === ResultsTypes.modelsOnly && !model) {
        const modelMap = new Map<number, IVehicleSearch>();
        const modelCount = new Map<number, number>();
        let lowestDistance = Infinity; // very high value to ensure it gets updated

        newFilteredList.forEach(vehicle => {
            const modelId = vehicle.vehicleModelData.id ?? -1;

            // record lowest distance to use for sorting
            if (vehicle.distance < lowestDistance) {
                lowestDistance = vehicle.distance;
            }

            // Increment the count for the current modelId
            modelCount.set(modelId, (modelCount.get(modelId) ?? 0) + 1);

            const currentCheapest = modelMap.get(modelId);
            const vehicleAvailable = vehicle.availabilityData?.available;
            const currentAvailable = currentCheapest?.availabilityData?.available;

            const isBetterRepresentative = !currentCheapest
                || (!currentAvailable && vehicleAvailable)
                || (vehicleAvailable === currentAvailable && (noVatPrices
                    ? vehicle.pricingDataNoVat.base.totalPrice < currentCheapest.pricingDataNoVat.base.totalPrice
                    : vehicle.pricingData.base.totalPrice < currentCheapest.pricingData.base.totalPrice));

            if (isBetterRepresentative) {
                modelMap.set(modelId, { ...vehicle, totalNumberOfVehicles: modelCount.get(modelId ?? -1) });
            }

        });

        // Update totalNumber for all entries in modelMap + distance value for model view result
        modelMap.forEach((vehicle, modelId) => {
            modelMap.set(modelId, { ...vehicle,
                totalNumberOfVehicles: modelCount.get(modelId ?? -1), distance: lowestDistance })
        });

        newFilteredList = Array.from(modelMap.values());
    }

    return newFilteredList;
}

/** True when the customer has narrowed the results beyond the plain car/van segment. */
export const hasActiveFilters = (params?: FilterParameters): boolean => !!params && (
    (!!params.types && params.types.length > 0) ||
    (!!params.fuelType && params.fuelType !== FuelType.All) ||
    (!!params.transmission && params.transmission !== Transmission.All) ||
    (!!params.tyreTypesNew && params.tyreTypesNew !== TyreTypes.All) ||
    (!!params.selectedTag && params.selectedTag !== Country.All) ||
    (!!params.otherTypes && params.otherTypes.length > 0) ||
    (!!params.model && params.model.length > 0) ||
    params.resultsType === ResultsTypes.modelsOnly
);

const useFilter = () => {
    const { mapData, filteredVehicles, filteredParams, sortingTab, searchParameters, changeFilterParams,changeFilteredList, acrissCodeFromUrl, modelId, acrissCodeSet, changeModelId, showingModelsResults, noVatPrices} = useContext(DataContext);
    const [originalList, setOriginalList] = useState<IVehicleSearch[]>(mapData ?? []);

    // Filter Tabs Values
    const [lowestPrice, setLowestPrice] = useState<number>(0);
    const [lowestDistance, setLowestDistance] = useState<number>(0);
    const [highestTrunkVolume, setHighestTrunkVolume] = useState<number>(0);

    useEffect(() => {
        // Derive stats from the individual-vehicle list (no model grouping) so they stay
        // stable when the user switches between sort tabs or toggles model view.
        const base = segmentBase(undefined, filteredParams?.types as VehicleType[]);
        if (base && base.length > 0) {
            const statsPool = applyFilterParameters(
                base,
                {...filteredParams, resultsType: ResultsTypes.All, model: undefined},
                acrissCodeSet,
                noVatPrices
            );
            if (statsPool.length > 0) {
                const available = statsPool.filter(v => v.availabilityData?.available);
                const pool = available.length > 0 ? available : statsPool;

                const closestVehicle = pool.reduce((prev, current) =>
                    current.distance < prev.distance ? current : prev);
                setLowestDistance(Math.round((+closestVehicle.distance / 1000) * 10) / 10);

                const lowestPriceVehicle = pool.reduce((prev, current) => {
                    const prevPricing = noVatPrices ? prev.pricingDataNoVat : prev.pricingData;
                    const currentPricing = noVatPrices ? current.pricingDataNoVat : current.pricingData;

                    const prevPrice = (prevPricing.withCoupon && !prev.privateData?.private)
                        ? prevPricing.withCoupon?.totalPrice
                        : prevPricing.base.totalPrice;

                    const currentPrice = (currentPricing.withCoupon && !current.privateData?.private)
                        ? currentPricing.withCoupon?.totalPrice
                        : currentPricing.base.totalPrice;

                    return currentPrice < prevPrice ? current : prev;
                });
                if (lowestPriceVehicle) {
                    const lowestPriceType = noVatPrices ? lowestPriceVehicle.pricingDataNoVat : lowestPriceVehicle.pricingData;
                    setLowestPrice(lowestPriceType?.withCoupon ?
                        lowestPriceType?.withCoupon?.totalPrice / 100 : basePrice(lowestPriceVehicle, noVatPrices));
                }

                const highestVolumeVehicle = pool.reduce((prev, current) =>
                    (current.vehicleModelData?.dimensions?.trunkVolume ?? 0) > (prev.vehicleModelData?.dimensions?.trunkVolume ?? 0)
                        ? current : prev);
                if (highestVolumeVehicle?.vehicleModelData?.dimensions?.trunkVolume !== undefined) {
                    setHighestTrunkVolume(highestVolumeVehicle.vehicleModelData.dimensions.trunkVolume);
                }
            }
        }
        sessionStorage.removeItem('sortWait');
    }, [filteredVehicles, noVatPrices, filteredParams, acrissCodeSet]);

    useEffect(() => {
        if (mapData) setOriginalList(mapData);
    }, [mapData]);

    const segmentBase = (source: IVehicleSearch[] = mapData ?? originalList, types?: VehicleType[]): IVehicleSearch[] => {
        if (types && types.length > 0) return source;
        const seg = searchParameters?.vehicleSegment;
        return seg === 'van' ? source.filter(v => v?.vehicleModelData?.type === 'van')
            : seg === 'car' ? source.filter(v => v?.vehicleModelData?.type !== 'van')
            : source;
    };

    const resetFilters = () => {
        changeFilterParams({types: [], fuelType: FuelType.All, tyreTypesNew: TyreTypes.All, selectedTag: Country.All,
            transmission: Transmission.All, tyreTypes: [], model: undefined, resultsType: ResultsTypes.All, reset: true});
        if (modelId) {
            changeModelId(undefined);
        }
        changeFilteredList(segmentBase());
    }

    // Pure sort — takes the list to order, so callers never publish a stale snapshot.
    const sortList = (list: IVehicleSearch[], option: SortOptions): IVehicleSearch[] => {
        let sortedVehicles: IVehicleSearch[] = [...list];
        sortedVehicles.sort((a, b) => {
            // Sort by reserved status first
            if (a.availabilityData?.available && !b.availabilityData?.available) return -1; // if a is reserved and b is not, sort a to the end
            if (!a.availabilityData?.available && b.availabilityData?.available) return 1; // if b is reserved and a is not, sort b to the end
            // Then sort by the selected option
            switch (option) {
                case SortOptions.Distance:
                    return a.distance - b.distance;
                case SortOptions.Models:
                case SortOptions.Price:
                    const aPricing = noVatPrices ? a.pricingDataNoVat : a.pricingData;
                    const bPricing = noVatPrices ? b.pricingDataNoVat : b.pricingData;
                    return (aPricing.withCoupon && !a.privateData?.private ? aPricing.withCoupon?.totalPrice : aPricing.base.totalPrice) -
                        (bPricing.withCoupon && !b.privateData?.private ? bPricing.withCoupon?.totalPrice : bPricing.base.totalPrice);
                case SortOptions.TrunkVolume:
                    return b.vehicleModelData!.dimensions!.trunkVolume! - a.vehicleModelData!.dimensions!.trunkVolume!;
                case SortOptions.Length:
                    // longest first — vehicleLength works for passenger cars too (trunk volume is 0 for many)
                    return (b.vehicleModelData?.dimensions?.vehicleLength ?? 0) - (a.vehicleModelData?.dimensions?.vehicleLength ?? 0);
                default:
                    return 0;
            }
        });
        return sortedVehicles;
    };

    const sortVehicles = (option: SortOptions) => {
        if (sessionStorage.getItem('sortWait') ?? undefined) return; // latest filteredVehicle state is not yet available
        // Re-derive from mapData + the active filters rather than re-ordering filteredVehicles:
        // this effect can run on a render where filteredVehicles is still the pre-filter list.
        const sortedVehicles = sortList(applyFilterParameters(segmentBase(undefined, filteredParams?.types as VehicleType[]), filteredParams, acrissCodeSet, noVatPrices), option);
        changeFilteredList(sortedVehicles);
        return sortedVehicles;
    };

    const handleFilters = (params: FilterParameters): IVehicleSearch[] | undefined => {
        const {types, fuelType, tyreTypesNew, selectedTag, transmission, otherTypes, model, resultsType} = params;
        // Filter mapData straight from context, not the `originalList` copy: that copy is synced
        // by an effect, so on the render where a new search lands it is still the previous
        // search's vehicles.
        const newFilteredList = applyFilterParameters(segmentBase(undefined, params.types), params, acrissCodeSet, noVatPrices);

        changeFilteredList(sortList(newFilteredList, sortingTab as SortOptions));
        changeFilterParams({types, fuelType, tyreTypesNew, selectedTag, transmission, otherTypes, model, resultsType, reset: false});

        return newFilteredList;
    }

    return { resetFilters, handleFilters, sortVehicles, lowestPrice, lowestDistance, highestTrunkVolume};
};

export default useFilter;